import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "@/App";
import { AppSidebar, SidebarMenuButton } from "@/pages/AppSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Building2, Plus, Trash2, Edit2, Users, X, ChevronDown,
  ChevronUp, Shield, UserPlus, Phone, Mail, Hash, Upload,
  Stethoscope, Link2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import DoctorAvailability from "@/components/DoctorAvailability";

const getToken = () => localStorage.getItem("session_token");
const headers = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

export default function AdminPage() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [members, setMembers] = useState({});
  const [showNewOrg, setShowNewOrg] = useState(false);
  const [newOrg, setNewOrg] = useState({ clinic_name: "", company_name: "", registry_code: "", email: "", phone: "" });
  const [editOrg, setEditOrg] = useState(null);
  const [newMember, setNewMember] = useState({ email: "", name: "", role: "user" });
  const [addingMemberTo, setAddingMemberTo] = useState(null);
  const [doctors, setDoctors] = useState({});
  const [showAddDoctor, setShowAddDoctor] = useState(null);
  const [newDoctor, setNewDoctor] = useState({ name: "", email: "" });
  const navigate = useNavigate();

  const isSuperAdmin = user?.user?.email === "verner.fogel@gmail.com" || user?.email === "verner.fogel@gmail.com";
  const userRole = user?.user?.role || user?.role || "user";
  const isOrgAdmin = userRole === "org_admin" || isSuperAdmin;

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/organizations`, { headers: headers() });
      if (res.ok) setOrgs(await res.json());
      else toast.error("Organisatsioonide laadimine ebaõnnestus");
    } catch { toast.error("Võrguühenduse viga"); }
    setLoading(false);
  }, []);

  useEffect(() => { if (isOrgAdmin) fetchOrgs(); else setLoading(false); }, [isOrgAdmin, fetchOrgs]);

  const fetchMembers = async (orgId) => {
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}/members`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setMembers(prev => ({ ...prev, [orgId]: data }));
      }
    } catch { /* ignore */ }
  };

  const fetchDoctors = async (orgId) => {
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}/doctors`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setDoctors(prev => ({ ...prev, [orgId]: data }));
      }
    } catch { /* ignore */ }
  };

  const toggleOrg = (orgId) => {
    if (expandedOrg === orgId) { setExpandedOrg(null); return; }
    setExpandedOrg(orgId);
    if (!members[orgId]) fetchMembers(orgId);
    if (!doctors[orgId]) fetchDoctors(orgId);
  };

  const createOrg = async () => {
    if (!newOrg.clinic_name || !newOrg.company_name) { toast.error("Kliiniku nimi ja firma nimi on kohustuslikud"); return; }
    try {
      const res = await fetch(`${API}/admin/organizations`, { method: "POST", headers: headers(), body: JSON.stringify(newOrg) });
      if (res.ok) { toast.success("Keskus loodud"); setShowNewOrg(false); setNewOrg({ clinic_name: "", company_name: "", registry_code: "", email: "", phone: "" }); fetchOrgs(); }
      else { const e = await res.json(); toast.error(e.detail || "Viga"); }
    } catch { toast.error("Võrguühenduse viga"); }
  };

  const updateOrg = async () => {
    if (!editOrg) return;
    try {
      const res = await fetch(`${API}/admin/organizations/${editOrg.org_id}`, {
        method: "PUT", headers: headers(),
        body: JSON.stringify({ clinic_name: editOrg.clinic_name, company_name: editOrg.company_name, registry_code: editOrg.registry_code, email: editOrg.email, phone: editOrg.phone }),
      });
      if (res.ok) { toast.success("Uuendatud"); setEditOrg(null); fetchOrgs(); }
      else { const e = await res.json(); toast.error(e.detail || "Viga"); }
    } catch { toast.error("Võrguühenduse viga"); }
  };

  const deleteOrg = async (orgId) => {
    if (!window.confirm("Kas olete kindel? Kõik keskuse andmed kustutatakse!")) return;
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}`, { method: "DELETE", headers: headers() });
      if (res.ok) { toast.success("Keskus kustutatud"); fetchOrgs(); }
    } catch { toast.error("Viga"); }
  };

  const addMember = async (orgId) => {
    if (!newMember.email) { toast.error("E-post on kohustuslik"); return; }
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}/members`, {
        method: "POST", headers: headers(), body: JSON.stringify(newMember),
      });
      if (res.ok) { toast.success("Kasutaja lisatud"); setNewMember({ email: "", name: "", role: "user" }); setAddingMemberTo(null); fetchMembers(orgId); fetchOrgs(); }
      else { const e = await res.json(); toast.error(e.detail || "Viga"); }
    } catch { toast.error("Võrguühenduse viga"); }
  };

  const removeMember = async (orgId, memberId) => {
    if (!window.confirm("Eemaldada see kasutaja?")) return;
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}/members/${memberId}`, { method: "DELETE", headers: headers() });
      if (res.ok) { toast.success("Kasutaja eemaldatud"); fetchMembers(orgId); fetchOrgs(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Kustutamine ebaõnnestus"); }
    } catch { toast.error("Võrguühenduse viga"); }
  };

  const updateMemberRole = async (orgId, memberId, newRole) => {
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}/members/${memberId}`, {
        method: "PUT", headers: headers(), body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) { toast.success("Roll uuendatud"); fetchMembers(orgId); }
      else { const e = await res.json(); toast.error(e.detail || "Viga"); }
    } catch { toast.error("Viga"); }
  };

  const addDoctor = async (orgId) => {
    if (!newDoctor.name || !newDoctor.email) { toast.error("Arsti nimi ja e-post on kohustuslikud"); return; }
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}/doctors`, {
        method: "POST", headers: headers(), body: JSON.stringify(newDoctor),
      });
      if (res.ok) { toast.success("Arst lisatud"); setNewDoctor({ name: "", email: "" }); setShowAddDoctor(null); fetchDoctors(orgId); }
      else { const e = await res.json(); toast.error(e.detail || "Viga"); }
    } catch { toast.error("Võrguühenduse viga"); }
  };

  const removeDoctor = async (orgId, doctorId) => {
    if (!window.confirm("Eemaldada see arst? Kõik õdede seosed kustutatakse.")) return;
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}/doctors/${doctorId}`, { method: "DELETE", headers: headers() });
      if (res.ok) { toast.success("Arst eemaldatud"); fetchDoctors(orgId); fetchMembers(orgId); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Viga"); }
    } catch { toast.error("Võrguühenduse viga"); }
  };

  const assignDoctorToNurse = async (orgId, memberId, doctorId) => {
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}/members/${memberId}/assign-doctor`, {
        method: "PUT", headers: headers(), body: JSON.stringify({ doctor_id: doctorId || null }),
      });
      if (res.ok) { toast.success(doctorId ? "Arst määratud" : "Arsti seos eemaldatud"); fetchMembers(orgId); fetchDoctors(orgId); }
      else { const e = await res.json(); toast.error(e.detail || "Viga"); }
    } catch { toast.error("Võrguühenduse viga"); }
  };

  if (!isOrgAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-700">Juurdepääs keelatud</h2>
          <p className="text-slate-500 mt-2">Ainult administraator saab keskusi hallata.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="md:ml-64">
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
          <Building2 className="w-5 h-5 text-slate-700" />
          <h1 className="text-lg font-bold text-slate-900" data-testid="admin-title">Keskuste haldus</h1>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => navigate("/admin/patient-data")} data-testid="patient-data-btn">
            <Shield className="w-4 h-4 mr-1" /> Patsiendi andmehaldus
          </Button>
          {isSuperAdmin && (
            <Button size="sm" onClick={() => setShowNewOrg(true)} data-testid="add-org-btn">
              <Plus className="w-4 h-4 mr-1" /> Lisa keskus
            </Button>
          )}
        </header>

        <div className="max-w-4xl mx-auto p-4 space-y-4">
          {/* Doctor Availability Settings */}
          <DoctorAvailability />

          {/* New org form */}
          {showNewOrg && (
            <div className="bg-white border border-blue-200 rounded-xl p-5 shadow-sm" data-testid="new-org-form">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-slate-900">Uus keskus</h3>
                <button onClick={() => setShowNewOrg(false)}><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input placeholder="Kliiniku nimi *" value={newOrg.clinic_name} onChange={e => setNewOrg(p => ({ ...p, clinic_name: e.target.value }))} data-testid="new-org-clinic-name" />
                <Input placeholder="Firma nimi *" value={newOrg.company_name} onChange={e => setNewOrg(p => ({ ...p, company_name: e.target.value }))} data-testid="new-org-company-name" />
                <Input placeholder="Registrikood" value={newOrg.registry_code} onChange={e => setNewOrg(p => ({ ...p, registry_code: e.target.value }))} data-testid="new-org-registry-code" />
                <Input placeholder="E-post" value={newOrg.email} onChange={e => setNewOrg(p => ({ ...p, email: e.target.value }))} data-testid="new-org-email" />
                <Input placeholder="Telefon" value={newOrg.phone} onChange={e => setNewOrg(p => ({ ...p, phone: e.target.value }))} data-testid="new-org-phone" />
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={createOrg} data-testid="submit-new-org">Lisa keskus</Button>
              </div>
            </div>
          )}

          {/* Edit org modal */}
          {editOrg && (
            <div className="bg-white border border-amber-200 rounded-xl p-5 shadow-sm" data-testid="edit-org-form">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-slate-900">Muuda: {editOrg.clinic_name}</h3>
                <button onClick={() => setEditOrg(null)}><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input placeholder="Kliiniku nimi" value={editOrg.clinic_name} onChange={e => setEditOrg(p => ({ ...p, clinic_name: e.target.value }))} />
                <Input placeholder="Firma nimi" value={editOrg.company_name} onChange={e => setEditOrg(p => ({ ...p, company_name: e.target.value }))} />
                <Input placeholder="Registrikood" value={editOrg.registry_code} onChange={e => setEditOrg(p => ({ ...p, registry_code: e.target.value }))} />
                <Input placeholder="E-post" value={editOrg.email} onChange={e => setEditOrg(p => ({ ...p, email: e.target.value }))} />
                <Input placeholder="Telefon" value={editOrg.phone} onChange={e => setEditOrg(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditOrg(null)}>Tühista</Button>
                <Button onClick={updateOrg}>Salvesta</Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12 text-slate-400">Laen...</div>
          ) : orgs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Keskusi pole veel lisatud</div>
          ) : (
            orgs.map(org => (
              <div key={org.org_id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`org-card-${org.org_id}`}>
                {/* Org header */}
                <div className="px-5 py-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => toggleOrg(org.org_id)}>
                  <Building2 className="w-5 h-5 text-blue-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">{org.clinic_name}</div>
                    <div className="text-sm text-slate-500">{org.company_name}</div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400 mr-3">
                    <Users className="w-4 h-4" /> {org.member_count || 0}
                  </div>
                  {isSuperAdmin && (
                    <div className="flex items-center gap-1 mr-4">
                      <button onClick={e => { e.stopPropagation(); setEditOrg({ ...org }); }} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`edit-org-${org.org_id}`}>
                        <Edit2 className="w-4 h-4 text-slate-400" />
                      </button>
                      <button onClick={e => { e.stopPropagation(); deleteOrg(org.org_id); }} className="p-1.5 hover:bg-red-50 rounded" data-testid={`delete-org-${org.org_id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  )}
                  {expandedOrg === org.org_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>

                {/* Org details */}
                {expandedOrg === org.org_id && (
                  <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/50">
                    {/* Org info + Logo */}
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                      {org.registry_code && <span className="flex items-center gap-1"><Hash className="w-3.5 h-3.5" />{org.registry_code}</span>}
                      {org.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{org.email}</span>}
                      {org.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{org.phone}</span>}
                    </div>
                    <LogoUpload orgId={org.org_id} hasLogo={!!org.logo_path} />

                    {/* ==================== DOCTORS SECTION ==================== */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-1.5">
                          <Stethoscope className="w-4 h-4 text-emerald-600" /> Arstid
                        </h4>
                        <Button size="sm" variant="outline" onClick={() => setShowAddDoctor(showAddDoctor === org.org_id ? null : org.org_id)} data-testid={`add-doctor-btn-${org.org_id}`}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Lisa arst
                        </Button>
                      </div>

                      {showAddDoctor === org.org_id && (
                        <div className="bg-white border border-emerald-100 rounded-lg p-3 mb-3" data-testid="add-doctor-form">
                          <div className="flex flex-wrap gap-2">
                            <Input placeholder="Arsti nimi *" className="flex-1 min-w-[160px]" value={newDoctor.name} onChange={e => setNewDoctor(p => ({ ...p, name: e.target.value }))} data-testid="new-doctor-name" />
                            <Input placeholder="E-post *" className="flex-1 min-w-[200px]" value={newDoctor.email} onChange={e => setNewDoctor(p => ({ ...p, email: e.target.value }))} data-testid="new-doctor-email" />
                            <Button size="sm" onClick={() => addDoctor(org.org_id)} data-testid="submit-new-doctor">Lisa</Button>
                          </div>
                        </div>
                      )}

                      <div className="space-y-1">
                        {(doctors[org.org_id] || []).map(doc => (
                          <div key={doc.doctor_id} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-emerald-100" data-testid={`doctor-${doc.doctor_id}`}>
                            <div className="w-8 h-8 bg-emerald-50 rounded-full flex items-center justify-center">
                              <Stethoscope className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900 truncate">{doc.name}</div>
                              <div className="text-xs text-slate-400 truncate">{doc.email}</div>
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                              {doc.nurse_count || 0} õde
                            </span>
                            <button onClick={() => removeDoctor(org.org_id, doc.doctor_id)} className="p-1 hover:bg-red-50 rounded" data-testid={`remove-doctor-${doc.doctor_id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        ))}
                        {(doctors[org.org_id] || []).length === 0 && (
                          <div className="text-sm text-slate-400 py-2">Arste pole veel lisatud</div>
                        )}
                      </div>
                    </div>

                    {/* ==================== MEMBERS SECTION ==================== */}
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm text-slate-700">Kasutajad</h4>
                      <Button size="sm" variant="outline" onClick={() => setAddingMemberTo(addingMemberTo === org.org_id ? null : org.org_id)} data-testid={`add-member-btn-${org.org_id}`}>
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Lisa kasutaja
                      </Button>
                    </div>

                    {/* Add member form */}
                    {addingMemberTo === org.org_id && (
                      <div className="bg-white border border-blue-100 rounded-lg p-3 mb-3" data-testid="add-member-form">
                        <div className="flex flex-wrap gap-2">
                          <Input placeholder="E-post *" className="flex-1 min-w-[200px]" value={newMember.email} onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))} data-testid="new-member-email" />
                          <Input placeholder="Nimi" className="w-40" value={newMember.name} onChange={e => setNewMember(p => ({ ...p, name: e.target.value }))} data-testid="new-member-name" />
                          <select className="border border-slate-200 rounded-md px-3 py-2 text-sm" value={newMember.role} onChange={e => setNewMember(p => ({ ...p, role: e.target.value }))} data-testid="new-member-role">
                            <option value="user">Kasutaja</option>
                            <option value="org_admin">Administraator</option>
                          </select>
                          <Button size="sm" onClick={() => addMember(org.org_id)} data-testid="submit-new-member">Lisa</Button>
                        </div>
                      </div>
                    )}

                    {/* Members list */}
                    <div className="space-y-1">
                      {(members[org.org_id] || []).map(m => {
                        const orgDoctors = doctors[org.org_id] || [];
                        const assignedDoc = orgDoctors.find(d => d.doctor_id === m.doctor_id);
                        return (
                          <div key={m.member_id} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-slate-100" data-testid={`member-${m.member_id}`}>
                            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-xs font-medium text-slate-600">
                              {m.email.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900 truncate">{m.name || m.email}</div>
                              <div className="text-xs text-slate-400 truncate">{m.email}</div>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.role === "org_admin" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                              {m.role === "org_admin" ? "Admin" : "Kasutaja"}
                            </span>
                            {/* Doctor assignment dropdown */}
                            {m.role !== "org_admin" && orgDoctors.length > 0 && (
                              <select
                                className="text-xs border rounded px-1.5 py-1 max-w-[140px] truncate"
                                value={m.doctor_id || ""}
                                onChange={e => assignDoctorToNurse(org.org_id, m.member_id, e.target.value)}
                                title={assignedDoc ? `Arst: ${assignedDoc.name}` : "Määra arst"}
                                data-testid={`assign-doctor-${m.member_id}`}
                              >
                                <option value="">— Arst —</option>
                                {orgDoctors.map(d => (
                                  <option key={d.doctor_id} value={d.doctor_id}>{d.name}</option>
                                ))}
                              </select>
                            )}
                            {assignedDoc && (
                              <span className="text-xs text-emerald-600 flex items-center gap-0.5" title={assignedDoc.email}>
                                <Link2 className="w-3 h-3" />
                              </span>
                            )}
                            <select className="text-xs border rounded px-1 py-0.5" value={m.role} onChange={e => updateMemberRole(org.org_id, m.member_id, e.target.value)} data-testid={`role-select-${m.member_id}`}>
                              <option value="user">Kasutaja</option>
                              <option value="org_admin">Admin</option>
                            </select>
                            <button onClick={() => removeMember(org.org_id, m.member_id)} className="p-1 hover:bg-red-50 rounded" data-testid={`remove-member-${m.member_id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        );
                      })}
                      {(members[org.org_id] || []).length === 0 && (
                        <div className="text-sm text-slate-400 py-2">Kasutajaid pole veel lisatud</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


function LogoUpload({ orgId, hasLogo }) {
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!hasLogo) return;
    const token = getToken();
    fetch(`${API}/admin/organizations/${orgId}/logo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(res => {
      if (res.ok) return res.blob();
      return null;
    }).then(blob => {
      if (blob) setLogoUrl(URL.createObjectURL(blob));
    }).catch(() => {});
  }, [orgId, hasLogo]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Fail on liiga suur (max 2MB)"); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const token = getToken();
      const res = await fetch(`${API}/admin/organizations/${orgId}/logo`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (res.ok) {
        toast.success("Logo üles laaditud");
        setLogoUrl(URL.createObjectURL(file));
      } else {
        const err = await res.json();
        toast.error(err.detail || "Üleslaadimine ebaõnnestus");
      }
    } catch { toast.error("Võrguühenduse viga"); }
    setUploading(false);
  };

  return (
    <div className="flex items-center gap-3 mb-4" data-testid={`logo-upload-${orgId}`}>
      {logoUrl ? (
        <img src={logoUrl} alt="Logo" className="w-12 h-12 rounded-lg object-contain border border-slate-200" />
      ) : (
        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
          <Building2 className="w-5 h-5 text-slate-300" />
        </div>
      )}
      <div>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          data-testid={`logo-btn-${orgId}`}
        >
          <Upload className="w-3.5 h-3.5" />
          {uploading ? "Laen..." : (logoUrl ? "Vaheta logo" : "Laadi logo üles")}
        </button>
        <p className="text-xs text-slate-400">PNG, JPG, WebP. Max 2MB</p>
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUpload} />
    </div>
  );
}
